import React, { useEffect, useRef, useState } from "react";
import Modal from "../../components/primitives/Modal";
import TextField from "../../components/primitives/TextField";
import TextArea from "../../components/primitives/TextArea";
import NumericField from "../../components/primitives/NumericField";
import TextButton from "../../components/primitives/TextButton";
import { createSku, updateSku, ApiError } from "../../services/api";
import { invalidateSkus, loadSkus } from "../../services/skuStore";

const NAME_MAX = 128;
const CODE_MAX = 64;
const DESC_MAX = 2000;
const DIM_MAX = 10000;

function blankForm() {
    return { name: "", sku_code: "", description: "", width: "", height: "", depth: "", weight: "" };
}

function fromSku(sku) {
    return {
        name: sku.name ?? "",
        sku_code: sku.sku_code ?? "",
        description: sku.description ?? "",
        width: sku.width ?? "",
        height: sku.height ?? "",
        depth: sku.depth ?? "",
        weight: sku.weight ?? "",
    };
}

function num(v) {
    const n = typeof v === "number" ? v : parseFloat(v);
    return Number.isFinite(n) ? n : NaN;
}

function validate(form) {
    const errs = {};
    const name = (form.name ?? "").trim();
    if (!name) errs.name = "Name is required.";
    else if (name.length > NAME_MAX) errs.name = `≤ ${NAME_MAX} characters.`;

    if (form.sku_code && form.sku_code.length > CODE_MAX) {
        errs.sku_code = `≤ ${CODE_MAX} characters.`;
    }

    if ((form.description ?? "").length > DESC_MAX) {
        errs.description = `≤ ${DESC_MAX} characters.`;
    }

    for (const k of ["width", "height", "depth"]) {
        const n = num(form[k]);
        if (!Number.isFinite(n) || n <= 0) {
            errs[k] = "Required, > 0.";
        } else if (n > DIM_MAX) {
            errs[k] = `≤ ${DIM_MAX}.`;
        }
    }

    if (form.weight !== "" && form.weight != null) {
        const n = num(form.weight);
        if (!Number.isFinite(n) || n <= 0) {
            errs.weight = "Must be greater than zero.";
        }
    }

    return errs;
}

function buildPayload(form, mode) {
    const trim = (s) => (typeof s === "string" ? s.trim() : s);
    const payload = {
        name: trim(form.name),
        width: num(form.width),
        height: num(form.height),
        depth: num(form.depth),
    };
    const code = trim(form.sku_code);
    if (code) payload.sku_code = code;
    else if (mode === "edit") payload.sku_code = null;

    const desc = form.description;
    if (desc && desc.length > 0) payload.description = desc;
    else if (mode === "edit") payload.description = null;

    if (form.weight !== "" && form.weight != null) {
        payload.weight = num(form.weight);
    } else if (mode === "edit") {
        payload.weight = null;
    }

    return payload;
}

function SkuFormModal({ open, mode = "create", initial, onClose, onSaved }) {
    const [form, setForm] = useState(blankForm);
    const [errors, setErrors] = useState({});
    const [serverErr, setServerErr] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const nameRef = useRef(null);

    useEffect(() => {
        if (open) {
            setForm(mode === "edit" && initial ? fromSku(initial) : blankForm());
            setErrors({});
            setServerErr(null);
        }
    }, [open, mode, initial]);

    const update = (field, value) => {
        setForm((f) => ({ ...f, [field]: value }));
        if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
        if (serverErr) setServerErr(null);
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setSubmitting(true);
        setServerErr(null);
        try {
            const payload = buildPayload(form, mode);
            const saved =
                mode === "edit" && initial
                    ? await updateSku(initial.id, payload)
                    : await createSku(payload);
            invalidateSkus();
            await loadSkus({ force: true });
            onSaved?.(saved);
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? err.message
                    : err?.message || "Save failed";
            setServerErr(msg);
            if (err?.details && Array.isArray(err.details)) {
                const fieldMap = {};
                err.details.forEach((d) => {
                    if (d.field && d.message) fieldMap[d.field] = d.message;
                });
                if (Object.keys(fieldMap).length) setErrors((e) => ({ ...e, ...fieldMap }));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const isEdit = mode === "edit";

    return (
        <Modal
            open={open}
            onClose={submitting ? undefined : onClose}
            eyebrow="Catalog"
            title={isEdit ? "Edit SKU" : "New SKU"}
            initialFocus={nameRef}
            footer={
                <>
                    <TextButton variant="ghost" onClick={onClose} disabled={submitting}>
                        Cancel
                    </TextButton>
                    <TextButton
                        variant="primary"
                        onClick={submit}
                        loading={submitting}
                    >
                        {isEdit ? "Save changes" : "Create SKU"}
                    </TextButton>
                </>
            }
        >
            <form onSubmit={submit} className="sku-form">
                <TextField
                    ref={nameRef}
                    label="Name"
                    required
                    value={form.name}
                    onChange={(v) => update("name", v)}
                    placeholder="Cereal box — large"
                    maxLength={NAME_MAX}
                    error={errors.name}
                    disabled={submitting}
                />
                <TextField
                    label="SKU code"
                    value={form.sku_code}
                    onChange={(v) => update("sku_code", v)}
                    placeholder="CRL-LRG-001"
                    maxLength={CODE_MAX}
                    error={errors.sku_code}
                    disabled={submitting}
                    hint="Optional. Free-form; useful for matching to your ERP."
                />
                <div className="sku-form__dims">
                    <NumericField
                        label="Width"
                        value={form.width}
                        onChange={(v) => update("width", v)}
                        hint={errors.width}
                    />
                    <NumericField
                        label="Height"
                        value={form.height}
                        onChange={(v) => update("height", v)}
                        hint={errors.height}
                    />
                    <NumericField
                        label="Depth"
                        value={form.depth}
                        onChange={(v) => update("depth", v)}
                        hint={errors.depth}
                    />
                </div>
                <NumericField
                    label="Weight"
                    unit="lb"
                    step={0.1}
                    value={form.weight}
                    onChange={(v) => update("weight", v)}
                    hint={errors.weight ?? "Optional. Used by future carrier-cost features."}
                />
                <TextArea
                    label="Description"
                    value={form.description}
                    onChange={(v) => update("description", v)}
                    placeholder="Notes for warehouse / pickers."
                    maxLength={DESC_MAX}
                    error={errors.description}
                    disabled={submitting}
                />
                {serverErr && (
                    <div className="sku-form__error" role="alert">
                        {serverErr}
                    </div>
                )}
                <button type="submit" hidden />
            </form>
        </Modal>
    );
}

export default SkuFormModal;
